import {isDefined} from '../../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {RuleBundleModelStrict, RuleBundlePrioritiesEnum} from '../../../../../models/api/ruleBundleModel';
import {RuleBundleTypesEnum} from '../../../../../models/api/ruleCommonTypes';
import {
  RuleBaseModelStrict,
  RuleCompoundConditions,
  RuleCompoundConditionTypesEnum,
  SimpleCompoundConditions,
} from '../../../../../models/api/ruleModel';
import {
  ActivityTypesEnum,
  BusinessHoursParametersEnum,
  DelayParameter,
} from '../../../../editors/commons/paramModel';
import {TransformerIdsEnum} from '../../../../editors/expressions/expressionInferHelpers';
import {ExpressionId} from '../../../../editors/expressions/expressionModel';
import {createRuleRecipeExpression} from '../../../library/recipes/builder';
import {buildRuleAction} from '../../../ruleActionHelpers';
import {RuleActionTypesEnum} from '../../../ruleActionModel';
import {buildRuleCondition} from '../../../ruleConditionHelpers';
import {RuleConditionTypesEnum} from '../../../ruleConditionModel';
import {eventsFromTriggers} from '../../../ruleTriggerHelpers';
import {RuleTriggersTypesEnum} from '../../../ruleTriggerModel';
import {SlaRuleRolesEnum} from '../../../slaRuleHelpers';
import {SlaEditorState, SlaTagsModels} from './state/slaEditorState';

const slaRelevantEvents: ReadonlyArray<RuleTriggersTypesEnum> = [
  RuleTriggersTypesEnum.INBOUND,
  RuleTriggersTypesEnum.MOVE,
];
const slaRemoveTagsEvents: ReadonlyArray<RuleTriggersTypesEnum> = [RuleTriggersTypesEnum.OUT_REPLY];

export function buildSlaBundleFromState(
  state: SlaEditorState,
  slaTags: SlaTagsModels,
  namespace: string,
): RuleBundleModelStrict {
  const recipeName = state.ruleState.name;

  // Rules for the Rule Bundle.
  const appliesTagRule = buildSlaAppliesTagRule(state, slaTags, recipeName, namespace);
  const breachTimerRule = buildSlaBreachTimerRule(state, recipeName, namespace);
  const warningTagRule = buildSlaWarningTagRule(state, recipeName, namespace);
  const breachTagRule = buildSlaBreachTagRule(state, slaTags, recipeName, namespace);
  const removeTagsRule = buildSlaRemoveTagsRule(state, slaTags, recipeName, namespace);
  const removeMetadataRule = buildSlaRemoveMetadataRule(state, slaTags, recipeName, namespace);

  // Build the bundle.
  const maybeBundle = state.slaState.initialRules?.bundle;

  return {
    id: maybeBundle?.id,
    name: state.ruleState.name,
    type: RuleBundleTypesEnum.SLA,
    priority: maybeBundle?.priority ?? RuleBundlePrioritiesEnum.DEFAULT,
    namespace: maybeBundle?.namespace ?? namespace,
    sortOrder: maybeBundle?.sortOrder,
    rules: [
      appliesTagRule,
      breachTimerRule,
      breachTagRule,
      warningTagRule,
      removeTagsRule,
      removeMetadataRule,
    ].filter(isDefined),
    url: undefined,
  };
}

function buildSlaAppliesTagRule(
  state: SlaEditorState,
  slaTags: SlaTagsModels,
  recipeName: string,
  namespace: string,
): RuleBaseModelStrict {
  const appliesTagId = slaTags.applies.id;
  const breachTagId = state.slaState.tags.breach;

  const orConditions = state.ruleState.conditions.map(
    (orCond): SimpleCompoundConditions => [RuleCompoundConditionTypesEnum.OR, ...orCond],
  );

  // Implicit SLA conditions that are not directly editable by the user.
  // If adding or removing any new conditions, update HIDDEN_FIRST_CONDITIONS
  const fixedConditions = [
    buildRuleCondition(RuleConditionTypesEnum.HASNT_TAG, {tag: appliesTagId}),
    buildRuleCondition(RuleConditionTypesEnum.HASNT_TAG, {tag: breachTagId}),
  ];

  return {
    id: state.slaState.initialRules?.appliesTagRule?.id,
    name: `${recipeName} - SLA: Applies`,
    namespace,
    events: eventsFromTriggers(slaRelevantEvents),
    expressions: [],
    compoundConditions: [RuleCompoundConditionTypesEnum.AND, ...fixedConditions, ...orConditions],
    actions: [buildRuleAction(RuleActionTypesEnum.TAG, {tag: appliesTagId})],
    active: state.ruleState.isActive,
    stopProcessing: false,
    sortOrder: 1,
    role: SlaRuleRolesEnum.APPLIES,
    description: '',
    footprintNamespaces: [namespace],
    lastTriggeredAt: undefined,
  };
}

function buildSlaBreachTimerRule(
  state: SlaEditorState,
  recipeName: string,
  namespace: string,
): RuleBaseModelStrict {
  const breachDelay = state.slaState.unreplied.breach.params.delay;
  if (!breachDelay) {
    throw new Error('Delay should be set for SLA rule');
  }

  const orConditions = state.ruleState.conditions.map(
    (orCondition): SimpleCompoundConditions => [RuleCompoundConditionTypesEnum.OR, ...orCondition],
  );

  return {
    id: state.slaState.initialRules?.breachTimerRule?.id,
    name: `${recipeName} - SLA Breach Timer`,
    namespace,
    events: eventsFromTriggers(slaRelevantEvents),
    expressions: [
      createRuleRecipeExpression('local:1', 'Breach Time', [
        {transform: TransformerIdsEnum.GET, params: {property: 'message'}},
        {transform: TransformerIdsEnum.GET, params: {property: 'date'}},
        {transform: TransformerIdsEnum.ADD, params: {period: breachDelay.unit, amount: breachDelay.val}},
        {transform: TransformerIdsEnum.TO_UNIX_TIMESTAMP, params: {}},
      ]),
    ],
    compoundConditions: [
      RuleCompoundConditionTypesEnum.AND,
      {
        integration_id: 0,
        type: RuleConditionTypesEnum.HAS_NOT_CONVERSATION_METADATA,
        params: {field: 'breach_time'},
      },
      ...orConditions,
    ],
    actions: [
      buildRuleAction(RuleActionTypesEnum.SET_CONVERSATION_METADATA, {
        field: 'breach_time',
        value: {template: [{expression: 'local:1' as ExpressionId}]},
      }),
    ],
    active: state.ruleState.isActive,
    stopProcessing: false,
    sortOrder: 3,
    role: SlaRuleRolesEnum.BREACH_TIMER,
    description: '',
    footprintNamespaces: [namespace],
    lastTriggeredAt: undefined,
  };
}

function buildSlaBreachTagRule(
  state: SlaEditorState,
  slaTags: SlaTagsModels,
  recipeName: string,
  namespace: string,
): RuleBaseModelStrict {
  const appliesTagId = slaTags.applies.id;
  const breachTagId = state.slaState.tags.breach;
  const warningTagId = state.slaState.tags.warning;

  if (!breachTagId) {
    throw new Error('SLA should have one breach tag');
  }

  const optionalBhBreachTitle =
    state.slaState.unreplied.breach.params?.business_hours === BusinessHoursParametersEnum.WITHIN_BH
      ? ' within business hours'
      : '';

  const breachDelay = state.slaState.unreplied.breach.params.delay;
  if (!breachDelay) {
    throw new Error('Delay should be set for SLA rule');
  }

  let actions = [
    buildRuleAction(RuleActionTypesEnum.UNTAG, {
      tag: appliesTagId,
    }),
    buildRuleAction(RuleActionTypesEnum.TAG, {tag: breachTagId}),
    ...state.slaState.actions.breach,
  ];

  // Untag 'warning tag' is a hidden action. It is this added at the beginning (easier to filter).
  if (warningTagId && hasWarning(state)) {
    actions = [buildRuleAction(RuleActionTypesEnum.UNTAG, {tag: warningTagId}), ...actions];
  }

  return {
    id: state.slaState.initialRules?.breachTagRule.id,
    name: `${recipeName} - SLA: Breach${optionalBhBreachTitle}`,
    namespace,
    events: eventsFromTriggers(slaRelevantEvents),
    expressions: [],
    compoundConditions: buildCompoundConditions(
      state,
      breachTagId,
      state.slaState.unreplied.breach.params.business_hours ?? BusinessHoursParametersEnum.REGARDLESS_BH,
      breachDelay,
    ),
    actions,
    active: state.ruleState.isActive,
    stopProcessing: false,
    sortOrder: 4,
    role: SlaRuleRolesEnum.BREACH,
    description: '',
    footprintNamespaces: [namespace],
    lastTriggeredAt: undefined,
  };
}

function buildSlaWarningTagRule(
  state: SlaEditorState,
  recipeName: string,
  namespace: string,
): RuleBaseModelStrict | undefined {
  const warningTagId = state.slaState.tags.warning;

  const warningDelay = state.slaState.unreplied.warning?.params.delay;
  const optionalBhWarningTitle =
    state.slaState.unreplied.warning?.params?.business_hours === BusinessHoursParametersEnum.WITHIN_BH
      ? ' within business hours'
      : '';

  if (!warningDelay || !warningTagId) {
    return undefined;
  }

  return {
    id: state.slaState.initialRules?.warningTagRule?.id,
    name: `${recipeName} - SLA: Warning${optionalBhWarningTitle}`,
    namespace,
    events: eventsFromTriggers(slaRelevantEvents),
    expressions: [],
    compoundConditions: buildCompoundConditions(
      state,
      warningTagId,
      state.slaState.unreplied.warning?.params.business_hours ?? BusinessHoursParametersEnum.REGARDLESS_BH,
      warningDelay,
    ),
    actions: [
      buildRuleAction(RuleActionTypesEnum.TAG, {tag: warningTagId}),
      ...state.slaState.actions.warning,
    ],
    active: state.ruleState.isActive,
    stopProcessing: false,
    sortOrder: 5,
    role: SlaRuleRolesEnum.WARNING,
    description: '',
    footprintNamespaces: [namespace],
    lastTriggeredAt: undefined,
  };
}

function buildSlaRemoveTagsRule(
  state: SlaEditorState,
  slaTags: SlaTagsModels,
  recipeName: string,
  namespace: string,
): RuleBaseModelStrict {
  const appliesTagId = slaTags.applies.id;
  const breachTagId = state.slaState.tags.breach;
  const warningTagId = state.slaState.tags.warning;

  if (!breachTagId) {
    throw new Error('SLA should have one breach tag');
  }

  let orCondition: SimpleCompoundConditions = [
    RuleCompoundConditionTypesEnum.OR,
    buildRuleCondition(RuleConditionTypesEnum.HAS_TAG, {tag: appliesTagId}),
    buildRuleCondition(RuleConditionTypesEnum.HAS_TAG, {tag: breachTagId}),
  ];
  let actions = [
    buildRuleAction(RuleActionTypesEnum.UNTAG, {tag: appliesTagId}),
    buildRuleAction(RuleActionTypesEnum.UNTAG, {tag: breachTagId}),
  ];

  if (warningTagId && hasWarning(state)) {
    orCondition = [
      ...orCondition,
      buildRuleCondition(RuleConditionTypesEnum.HAS_TAG, {
        tag: warningTagId,
      }),
    ];
    actions = [...actions, buildRuleAction(RuleActionTypesEnum.UNTAG, {tag: warningTagId})];
  }

  return {
    id: state.slaState.initialRules?.removeTagsRule?.id,
    name: `${recipeName} - SLA: Remove breach/warning tags on reply`,
    namespace,
    events: eventsFromTriggers(slaRemoveTagsEvents),
    expressions: [],
    compoundConditions: [
      RuleCompoundConditionTypesEnum.AND,
      buildRuleCondition(RuleConditionTypesEnum.IS_EARLIEST_SLA_BREACH, {}),
      orCondition,
    ],
    actions,
    active: state.ruleState.isActive,
    stopProcessing: false,
    sortOrder: 6,
    role: SlaRuleRolesEnum.REMOVE_TAG_ON_REPLY,
    description: '',
    footprintNamespaces: [namespace],
    lastTriggeredAt: undefined,
  };
}

function buildSlaRemoveMetadataRule(
  state: SlaEditorState,
  slaTags: SlaTagsModels,
  recipeName: string,
  namespace: string,
): RuleBaseModelStrict {
  return {
    id: state.slaState.initialRules?.removeMetadataRule?.id,
    name: `${recipeName} - SLA: Rm Metadata on Reply`,
    namespace,
    events: eventsFromTriggers(slaRemoveTagsEvents),
    expressions: [],
    compoundConditions: [
      RuleCompoundConditionTypesEnum.AND,
      buildRuleCondition(RuleConditionTypesEnum.HAS_CONVERSATION_METADATA, {field: 'breach_time'}),
    ],
    actions: [
      buildRuleAction(RuleActionTypesEnum.SET_CONVERSATION_METADATA, {
        field: 'breach_time',
        value: 0,
      }),
    ],
    active: state.ruleState.isActive,
    stopProcessing: false,
    sortOrder: 7,
    role: SlaRuleRolesEnum.REMOVE_METADATA_ON_REPLY,
    description: '',
    footprintNamespaces: [namespace],
    lastTriggeredAt: undefined,
  };
}

function buildCompoundConditions(
  state: SlaEditorState,
  tagId: number,
  businessHours: BusinessHoursParametersEnum,
  delay: DelayParameter,
): RuleCompoundConditions {
  const orConditions = state.ruleState.conditions.map(
    (orCondition): SimpleCompoundConditions => [RuleCompoundConditionTypesEnum.OR, ...orCondition],
  );

  // Implicit SLA conditions that are not directly editable by the user.
  // If adding or removing any new conditions, update HIDDEN_FIRST_CONDITIONS
  const fixedConditions = [
    buildRuleCondition(RuleConditionTypesEnum.HASNT_TAG, {tag: tagId}),
    buildRuleCondition(RuleConditionTypesEnum.HAS_CONVERSATION_METADATA, {field: 'breach_time'}),
    buildRuleCondition(RuleConditionTypesEnum.UNREPLIED_AFTER, {
      delay,
      business_hours: businessHours,
      activity_type: ActivityTypesEnum.INBOUND,
    }),
  ];

  return [RuleCompoundConditionTypesEnum.AND, ...fixedConditions, ...orConditions];
}

function hasWarning(state: SlaEditorState) {
  return state.slaState.unreplied.warning !== undefined;
}
